/**
 * useArcadeClock — a requestAnimationFrame countdown shared by the arcade
 * mini-games.
 *
 * Follows the browser game-loop best practices: the loop schedules the next
 * frame before doing work, accumulates real elapsed time from
 * `performance.now()`, caps the per-frame delta to avoid the "spiral of
 * death", and pauses automatically when the tab is hidden (rAF callbacks are
 * suspended there anyway, so we also freeze the timestamp baseline). Loop
 * bookkeeping lives in refs; React state is updated at a modest cadence so the
 * countdown re-renders smoothly without a 60 Hz render storm. The rAF handle is
 * always cancelled on unmount.
 *
 * The clock is presentation only — it never decides a game's structural
 * verdict; it adds time pressure and drives the timer bar.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** Largest delta (seconds) we will apply in one tick — spiral-of-death guard. */
const MAX_DELTA = 0.25;

/** Minimum change (seconds) before we push a new React state value. */
const RENDER_STEP = 0.05;

export interface ArcadeClock {
  /** Seconds remaining, clamped to [0, duration]. */
  readonly remaining: number;
  /** Fraction remaining in [0, 1] for a timer bar width. */
  readonly fraction: number;
  /** Whether the loop is currently ticking. */
  readonly running: boolean;
  /** True once the countdown has reached zero. */
  readonly expired: boolean;
  /** Start (or resume) the countdown. */
  readonly start: () => void;
  /** Pause the countdown, keeping the remaining time. */
  readonly pause: () => void;
  /** Reset to `duration` (or a new duration) and stop. */
  readonly reset: (nextDuration?: number) => void;
}

export function useArcadeClock(
  durationSeconds: number,
  onExpire?: () => void,
): ArcadeClock {
  const [remaining, setRemaining] = useState(durationSeconds);
  const [running, setRunning] = useState(false);

  const durationRef = useRef(durationSeconds);
  const remainingRef = useRef(durationSeconds);
  const lastEmittedRef = useRef(durationSeconds);
  const runningRef = useRef(false);
  const lastTsRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // If the caller changes the duration while the clock is not running, sync the
  // remaining time to the new duration. A running countdown is left to finish;
  // use reset() to rearm it explicitly.
  useEffect(() => {
    if (runningRef.current) return;
    durationRef.current = durationSeconds;
    remainingRef.current = durationSeconds;
    lastEmittedRef.current = durationSeconds;
    setRemaining(durationSeconds);
  }, [durationSeconds]);

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTsRef.current = null;
  }, []);

  const tick = useCallback(
    (ts: number) => {
      // Schedule the next frame first, so a slow frame still re-queues.
      rafRef.current = requestAnimationFrame(tick);
      if (!runningRef.current) return;
      const last = lastTsRef.current;
      lastTsRef.current = ts;
      if (last === null) return;
      const dt = Math.min(MAX_DELTA, (ts - last) / 1000);
      const next = Math.max(0, remainingRef.current - dt);
      remainingRef.current = next;
      if (
        Math.abs(lastEmittedRef.current - next) >= RENDER_STEP ||
        next === 0
      ) {
        lastEmittedRef.current = next;
        setRemaining(next);
      }
      if (next === 0) {
        runningRef.current = false;
        setRunning(false);
        stopLoop();
        onExpireRef.current?.();
      }
    },
    [stopLoop],
  );

  const start = useCallback(() => {
    if (runningRef.current || remainingRef.current <= 0) return;
    runningRef.current = true;
    setRunning(true);
    lastTsRef.current = null;
    rafRef.current ??= requestAnimationFrame(tick);
  }, [tick]);

  const pause = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
    stopLoop();
  }, [stopLoop]);

  const reset = useCallback(
    (nextDuration?: number) => {
      const d = nextDuration ?? durationRef.current;
      durationRef.current = d;
      remainingRef.current = d;
      lastEmittedRef.current = d;
      runningRef.current = false;
      setRunning(false);
      setRemaining(d);
      stopLoop();
    },
    [stopLoop],
  );

  // Freeze the baseline when the tab is hidden so time does not "jump" on
  // return; the next visible frame re-establishes lastTs.
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.hidden) lastTsRef.current = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return (): void => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Always cancel the loop on unmount.
  useEffect((): (() => void) => stopLoop, [stopLoop]);

  const d = durationRef.current;
  return {
    remaining,
    fraction: d > 0 ? Math.max(0, Math.min(1, remaining / d)) : 0,
    running,
    expired: remaining <= 0,
    start,
    pause,
    reset,
  };
}
